import React, { useEffect, useMemo, useState } from "react";
import { get } from "lodash";
import { useNavigate } from "react-router-dom";

import styles from "./PlanDetail.module.scss";

import { columns } from "../../config/PlanDetailPage.config";
import Table from "../table/Table";
import Button from "../button/Button";
import PopUp from "../popUp/PopUp";
import userStore from "../../zustand/user.store";
import { formateTimeStampDate } from "../../helpers/generel.helpers";
import { ROUTES } from "../../constants/routes.constants";
import {
  cancelPendingPlanChangeAction,
  cancelSubscriptionAction,
  fetchBillingStatusAction,
  fetchTransactionsAction,
  getPaymentMethodUpdateUrlAction,
  resumeSubscriptionAction,
} from "../../actions/user.actions";
import { generalConstants } from "../../constants";

const PlanDetail = () => {
  const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isCancellingPending, setIsCancellingPending] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const navigate = useNavigate();

  const { user, userOffline } = userStore((state) => ({
    user: state.user,
    userOffline: state.userOffline,
  }));

  const refreshBillingStatus = async () => {
    const res = await fetchBillingStatusAction(userOffline);
    if (res?.success) setBillingStatus(get(res, "data", null));
  };

  const refreshTransactions = async () => {
    setIsLoadingTransactions(true);
    const res = await fetchTransactionsAction(
      { page: 1, limit: 20 },
      userOffline,
    );
    if (res?.success) {
      const items = get(res, "data.items", null);
      if (Array.isArray(items)) {
        setTransactions(items);
      }
    }
    setIsLoadingTransactions(false);
  };

  useEffect(() => {
    refreshBillingStatus();
    refreshTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOffline]);

  const handleChangePlan = () => {
    navigate(ROUTES.PLANS);
  };

  const handleCancelSubscription = async () => {
    setIsLoading(true);
    const res = await cancelSubscriptionAction();
    if (res.success) {
      await refreshBillingStatus();
      setIsCancelPopupOpen(false);
    }
    setIsLoading(false);
  };

  const handleResumeSubscription = async () => {
    setIsResuming(true);
    const res = await resumeSubscriptionAction(userOffline);
    if (res?.success) {
      await refreshBillingStatus();
    }
    setIsResuming(false);
  };

  const handleCancelPendingPlanChange = async () => {
    setIsCancellingPending(true);
    const res = await cancelPendingPlanChangeAction(userOffline);
    if (res?.success) {
      await refreshBillingStatus();
    }
    setIsCancellingPending(false);
  };

  const handleUpdatePaymentMethod = async () => {
    setIsUpdatingPayment(true);
    const directUrl = get(billingStatus, "payment_method_update_url", null);
    if (directUrl) {
      window.location.href = directUrl;
    } else {
      const res = await getPaymentMethodUpdateUrlAction(userOffline);
      if (res?.success) {
        window.location.href = res.data.url;
      }
    }
    setIsUpdatingPayment(false);
  };

  const planType =
    get(billingStatus, "plan_type", null) || get(user, "primium_type", "");
  // billing-status may come back as 304/empty; fall back to user fields
  const subscriptionStatus =
    get(billingStatus, "subscription_status", null) ||
    get(user, "subscription_status", null);

  const pricing = useMemo(() => {
    const apiPricing = get(billingStatus, "pricing", null);
    return {
      radio_monthly:
        get(apiPricing, "radio_monthly", null) ??
        generalConstants.PLAN_TYPE_VS_PRICE["RADIO"],
      unlimited_monthly:
        get(apiPricing, "unlimited_monthly", null) ??
        generalConstants.PLAN_TYPE_VS_PRICE["UNLIMITED"],
      trial_days: get(apiPricing, "trial_days", null) ?? 3,
    };
  }, [billingStatus]);

  const isPaidUser = planType === "UNLIMITED" || planType === "RADIO";
  const actions = get(billingStatus, "actions", {});
  const cancelAtPeriodEnd = get(billingStatus, "cancel_at_period_end", false);
  const cancelEffectiveAt = get(billingStatus, "cancel_effective_at", null);
  const pendingPlanType = get(billingStatus, "pending_plan_type", null);
  const pendingPlanEffectiveAt = get(
    billingStatus,
    "pending_plan_effective_at",
    null,
  );
  const billingErrorCount =
    get(billingStatus, "billing_error_count", null) ??
    get(user, "billing_error_count", 0);

  // Determine UI state based on subscription status
  const isPastDue = subscriptionStatus === "PAST_DUE";
  const isPaused = subscriptionStatus === "PAUSED";
  const isCanceled = subscriptionStatus === "CANCELED";
  const isNone = subscriptionStatus === "NONE";
  const needsAction = isPastDue || isPaused || isCanceled || isNone;

  // Get display text based on subscription status
  const getStatusDisplay = () => {
    if (isPastDue) return "Payment Required";
    if (isPaused) return "Action Required";
    if (isCanceled) return "Canceled";
    if (isNone) return "No Active Plan";
    return subscriptionStatus;
  };

  const getHeadingText = () => {
    if (isPastDue) return "Subscription Needs Attention";
    if (isPaused) return `${planType || "Your"} Plan - Paused`;
    if (isCanceled) return "Plan Ended";
    if (isNone) return "Get Started";
    return `${planType || "FREE"} PLAN`;
  };

  return (
    <div className={styles.PlanDetail}>
      <PopUp
        isOpen={isCancelPopupOpen}
        setIsOpen={setIsCancelPopupOpen}
        title="Are you sure you want to cancel your subscription?"
        iconType="RED_DELETE"
        btnPlaceholder="Confirm"
        onSuccess={handleCancelSubscription}
        isLoading={isLoading}
      />
      <div className={styles.headerContainer}>
        <div className={styles.header}>
          <h1 className={styles.heading}>{getHeadingText()}</h1>

          {/* Status badge with appropriate styling */}
          {subscriptionStatus && (
            <p
              className={`${styles.title} ${needsAction ? styles.warningText : ""}`}
            >
              {`Status: ${getStatusDisplay()}`}
            </p>
          )}

          {/* PAST_DUE specific messaging */}
          {isPastDue && (
            <>
              <p className={`${styles.title} ${styles.errorText}`}>
                {billingErrorCount > 1
                  ? `Payment failed (${billingErrorCount} attempts). Please update your payment method to restore access.`
                  : "Your payment couldn't be processed. Update your payment method to continue using the player."}
              </p>
            </>
          )}

          {/* PAUSED specific messaging */}
          {isPaused && (
            <p className={`${styles.title} ${styles.warningText}`}>
              Your subscription is paused. Complete checkout to restore access.
            </p>
          )}

          {/* CANCELED specific messaging */}
          {isCanceled && (
            <p className={styles.title}>
              Your subscription has ended. Subscribe again to access all
              features.
            </p>
          )}

          {/* NONE specific messaging */}
          {isNone && (
            <p className={styles.title}>
              Choose a plan to get started with unlimited music for your
              business.
            </p>
          )}

          {/* Trial info (only for active trials) */}
          {get(billingStatus, "trial_ends_at", null) &&
            subscriptionStatus === "TRIAL" && (
              <p
                className={styles.title}
              >{`Trial ends on ${formateTimeStampDate(
                get(billingStatus, "trial_ends_at"),
              )} (${pricing.trial_days} days)`}</p>
            )}

          {/* Next billing date (only for healthy subscriptions that will renew) */}
          {get(billingStatus, "next_billing_date", null) &&
            isPaidUser &&
            !isPastDue &&
            !isPaused &&
            !cancelAtPeriodEnd && (
              <p className={styles.title}>
                {`Your next billing date is ${formateTimeStampDate(
                  get(billingStatus, "next_billing_date"),
                )}`}
              </p>
            )}

          {/* Cancellation scheduled */}
          {cancelAtPeriodEnd && cancelEffectiveAt && (
            <p className={styles.title}>
              {`Cancellation scheduled. Access until ${formateTimeStampDate(
                cancelEffectiveAt,
              )}.`}
            </p>
          )}

          {/* Pending plan change */}
          {pendingPlanType && pendingPlanEffectiveAt && !isPaused && (
            <p className={styles.title}>
              {`Plan scheduled to change to ${pendingPlanType} on ${formateTimeStampDate(
                pendingPlanEffectiveAt,
              )}.`}
            </p>
          )}
        </div>
        <div className={styles.subManagement}>
          {/* Only show price for active/trial users */}
          {!needsAction && (
            <div className={styles.price}>
              <h1 className={styles.text1}>
                {planType === "UNLIMITED"
                  ? `$${pricing.unlimited_monthly}`
                  : planType === "RADIO"
                    ? `$${pricing.radio_monthly}`
                    : "FREE"}
              </h1>
              <p className={styles.text2}> / month</p>
            </div>
          )}
          <div className={styles.actionBtns}>
            {/* Primary CTA for PAST_DUE: Update Payment Method (most important action) */}
            {isPastDue && (
              <>
                <Button
                  type="primary"
                  className={`${styles.btn} ${styles.primaryAction}`}
                  onClick={handleUpdatePaymentMethod}
                  isLoading={isUpdatingPayment}
                >
                  Continue Plan
                </Button>
                <Button
                  type="Secondary"
                  className={styles.btn}
                  onClick={() => navigate(ROUTES.PACKAGES)}
                >
                  Select a Plan
                </Button>
              </>
            )}

            {/* Primary CTA for CANCELED/NONE: Subscribe/Resubscribe */}
            {(isCanceled || isNone) && (
              <Button
                type="primary"
                className={`${styles.btn} ${styles.primaryAction}`}
                onClick={handleChangePlan}
              >
                {isCanceled ? "Resubscribe" : "Choose a Plan"}
              </Button>
            )}

            {/* Change Plan button (for active users, not past due) */}
            {get(actions, "can_change_plan", false) &&
              !isPastDue &&
              !isCanceled &&
              !isNone && (
                <Button
                  type="Secondary"
                  className={styles.btn}
                  onClick={handleChangePlan}
                >
                  Change Plan
                </Button>
              )}

            {/* Cancel Subscription button */}
            {get(actions, "can_cancel", false) && (
              <Button
                type="primary"
                className={styles.btn}
                onClick={() => setIsCancelPopupOpen(true)}
              >
                Cancel Subscription
              </Button>
            )}

            {/* Undo Cancellation button */}
            {get(actions, "can_resume", false) && (
              <Button
                type="primary"
                className={styles.btn}
                onClick={handleResumeSubscription}
                isLoading={isResuming}
              >
                Undo Cancellation
              </Button>
            )}

            {/* Cancel Scheduled Downgrade button */}
            {pendingPlanType && !isPaused && (
              <Button
                type="primary"
                className={styles.btn}
                onClick={handleCancelPendingPlanChange}
                isLoading={isCancellingPending}
              >
                Cancel Scheduled Downgrade
              </Button>
            )}

            {/* Update Payment Method for healthy paid users (secondary) */}
            {isPaidUser &&
              !isPastDue &&
              get(billingStatus, "payment_method_update_url", null) && (
                <Button
                  type="Secondary"
                  className={styles.btn}
                  onClick={handleUpdatePaymentMethod}
                  isLoading={isUpdatingPayment}
                >
                  Update Payment Method
                </Button>
              )}
          </div>
        </div>
      </div>
      <div className={styles.PlanDetailContainer}>
        <h2 className={styles.transactionHeader}>Transaction History</h2>
        {isLoadingTransactions ? (
          <p className={styles.noTransactions}>Loading transactions...</p>
        ) : (transactions && transactions.length > 0) ||
          (user.transactions && user.transactions.length > 0) ? (
          <Table
            rows={transactions.length ? transactions : user.transactions}
            columns={columns}
          />
        ) : (
          <p className={styles.noTransactions}>
            No transaction history available.
          </p>
        )}
      </div>
    </div>
  );
};

export default PlanDetail;
